import type { ErrorEvent } from '@sentry/react';

/**
 * How long one report buys quiet for the rest of its kind.
 *
 * Client-side failures arrive in bursts rather than one at a time: a route fans
 * its queries out together, so whatever answers the first one answers all of
 * them within a few hundred milliseconds. The report that prompted this was a
 * user on `/dashboard` tripping Cloudflare's rate limit — eight 429s inside a
 * single second, which without this would be eight identical Sentry events
 * carrying no more information than the first one did.
 *
 * Ten seconds is long enough to swallow a page load's worth of fan-out and
 * short enough that a failure the user is actively retrying still reports more
 * than once, so an issue's event count stays a usable measure of how often it
 * happens rather than collapsing to one.
 */
const WINDOW_MS = 10_000;

/**
 * Distinct error kinds tracked at once, so a long-lived tab can't accumulate a
 * map entry per unique error message forever. Only reached if the app produces
 * this many *different* errors within one window, and the expired entries are
 * dropped when it is — so what actually bounds the map is the number of
 * distinct errors in the last ten seconds.
 */
const MAX_TRACKED_KEYS = 500;

type ReportWindow = {
  openedAt: number;
  /** Same-key events dropped since the report that opened this window. */
  suppressed: number;
};

const openWindows = new Map<string, ReportWindow>();

/**
 * What counts as "the same error" here. Mirrors how Sentry itself groups: the
 * explicit fingerprint when the call site set one (the GraphQL captures in
 * `query-client.ts` all do, and theirs are deliberately coarse so one backend
 * failure is one issue), otherwise the exception type and message, otherwise
 * the message of a `captureMessage`.
 *
 * `undefined` when an event has none of those, which reports rather than being
 * lumped together with every other unkeyable event under a shared fallback.
 */
const getEventKey = (event: ErrorEvent): string | undefined => {
  if (event.fingerprint?.length) {
    return event.fingerprint.join('|');
  }

  const exception = event.exception?.values?.[0];
  if (exception?.type || exception?.value) {
    return `${exception.type ?? ''}: ${exception.value ?? ''}`;
  }

  return event.message;
};

const pruneExpired = (now: number) => {
  if (openWindows.size < MAX_TRACKED_KEYS) {
    return;
  }

  for (const [key, { openedAt }] of openWindows) {
    if (now - openedAt >= WINDOW_MS) {
      openWindows.delete(key);
    }
  }
};

/**
 * Collapses a burst of the same error into one report per window. Returns the
 * event to keep it, or `null` to drop it.
 *
 * The first event of a window is reported immediately, so nothing is delayed
 * and a one-off failure still surfaces the moment it happens. Everything
 * matching it for the next `WINDOW_MS` is dropped and counted, and that count
 * rides out on the next report of the same key as
 * `suppressedSincePreviousReport`. A burst that never recurs therefore loses
 * its tail — the tradeoff for not holding the first report back behind a timer
 * that a tab close would discard anyway.
 *
 * This is not Sentry's own `dedupeIntegration`, which we still get: that only
 * compares an event against the one immediately before it, so two operations
 * failing in an A-B-A-B interleave — exactly what a fan-out burst looks like —
 * defeat it entirely.
 */
export const rateLimitSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  const key = getEventKey(event);
  if (!key) {
    return event;
  }

  const now = Date.now();
  const previous = openWindows.get(key);

  if (previous && now - previous.openedAt < WINDOW_MS) {
    previous.suppressed += 1;
    return null;
  }

  pruneExpired(now);
  openWindows.set(key, { openedAt: now, suppressed: 0 });

  if (previous?.suppressed) {
    event.extra = {
      ...event.extra,
      suppressedSincePreviousReport: previous.suppressed,
    };
  }

  return event;
};

/** Test seam: drops the tracked windows so cases can't leak into each other. */
export const resetSentryRateLimit = () => {
  openWindows.clear();
};
